import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Building2, ArrowRight, Bus, MapPin, Users } from "lucide-react";

export default function Home() {
  const [operadores, setOperadores] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = useCallback(async () => {
    const res = await api.get("/operadores");
    setOperadores(res.data);
    if (user?.role === "operador" && res.data.length === 1) {
      navigate(`/operador/${res.data[0].id}`, { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => { load(); }, [load]);

  return (
    <AppShell operadores={operadores} reloadOperadores={load}>
      <header className="h-16 flex items-center justify-between px-6 border-b border-border bg-card sticky top-0 z-10">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Work Sans" }}>Panel de Operadores</h1>
          <p className="text-xs text-muted-foreground">Gestión de datos de servicios urbanos · ATTG</p>
        </div>
      </header>

      <div className="flex-1 overflow-auto attg-scroll p-6">
        {operadores.length === 0 ? (
          <div className="max-w-md mx-auto text-center mt-24" data-testid="home-empty">
            <div className="h-14 w-14 rounded-2xl bg-accent text-accent-foreground flex items-center justify-center mx-auto mb-4">
              <Building2 size={26} />
            </div>
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>Aún no hay operadores</h2>
            <p className="text-sm text-muted-foreground mt-1">Crea tu primer operador desde la barra lateral para empezar a cargar sus datos.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="operador-grid">
            {operadores.map((op) => (
              <button
                key={op.id}
                onClick={() => navigate(`/operador/${op.id}`)}
                data-testid={`operador-card-${op.id}`}
                className="text-left rounded-xl border bg-card shadow-sm hover:shadow-md hover:border-primary/40 transition-all p-5 group"
              >
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 rounded-lg bg-accent text-accent-foreground flex items-center justify-center">
                    <Building2 size={20} />
                  </div>
                  <ArrowRight size={18} className="text-slate-300 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                </div>
                <h3 className="mt-3 font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>{op.nombre}</h3>
                <p className="text-xs text-muted-foreground">{op.nombre_corto}{op.codigo_operador ? ` · Cód. ${op.codigo_operador}` : ""}</p>
                <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1"><Bus size={13} /> {op.counts?.lineas ?? 0} líneas</span>
                  <span className="inline-flex items-center gap-1"><MapPin size={13} /> {op.counts?.paradas ?? 0} paradas</span>
                  <span className="inline-flex items-center gap-1"><Users size={13} /> {op.counts?.empleados ?? 0}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
